/**
 * Shared SSN formatter for the Teams (internal) app.
 *
 * Per feedback_ssn_masking.md: internal users see FULL SSN (not masked).
 * Masking is reserved for customer-facing surfaces (RCP portal).
 *
 * `formatSsn` accepts any raw digit string (with or without dashes) and
 * returns the canonical `123-45-6789` shape. Returns an em dash for
 * missing / malformed input.
 */
export function formatSsn(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 9) return raw; // fall back to caller-supplied shape
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/**
 * Restore a leading zero lost in transit from Core.
 *
 * jXchange returns TaxId as a JSON NUMBER for some CIF records, and JSON
 * numbers cannot carry a leading zero — by the time the value reaches us the
 * digit is already gone. A tax id that arrives as 8 digits is therefore a
 * 9-digit id whose leading zero was dropped, not a genuine 8-digit value:
 * every US SSN, ITIN and EIN is exactly 9 digits.
 *
 * Symptom this fixes (Bill 2026-09-23): Core showed 0XX-XX-XXXX, the person
 * modal carried it one digit short, and LexisNexis rejected the query with
 * "Please insert a complete, properly formatted SSN" — so identity
 * verification could not run at all for those customers.
 *
 * Deliberately narrow. It pads ONLY an all-digit value of 1-8 characters, so
 * a formatted id, an already-9-digit id, or anything carrying a non-digit is
 * returned untouched rather than reshaped into something plausible but wrong.
 */
export function restoreTaxIdLeadingZeros(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!/^\d{1,8}$/.test(trimmed)) return trimmed;
  return trimmed.padStart(9, '0');
}
